import styled from "@emotion/styled";
import { Header } from "../../../components/typography/Header";
import { StyledDescription } from "../../questionnaire/Questionnaire";
import { PrimaryButton } from "../../../components/buttons/PrimaryButton";
import { useSendQuestionnaireReminderMutation } from "../../../api/users";
import { FONT_SIZE_SMALL, SPACING } from "../../../components/theme";
import { User } from "../../../../../api/src/types";
import { StyledLink } from "../../../components/layout/attorneys/components";

const StyledHeader = styled(Header)`
  margin-bottom: ${SPACING.MD};
`;

const StyledSubtitle = styled(StyledDescription)`
  margin-bottom: ${SPACING.LG};
`;

const StyledButtonContainer = styled.div`
  width: 70%;
  margin: ${SPACING.MD} auto;
`;

interface NegotiationHeaderProps {
  hasDisputes: boolean;
  isPartnerQuestionnaireComplete: boolean;
  isCurrentUserQuestionnaireComplete: boolean;
  partnerName: string | null;
  user: User | undefined;
  areAccountsLinked: boolean;
  onViewHistory: () => void;
  showHistoryLink: boolean;
  onNavigateToIncompleteSection?: () => void;
  currentUserHasFinancials: boolean;
  partnerHasFinancials: boolean;
}

const NegotiationHeader = ({
  hasDisputes,
  isPartnerQuestionnaireComplete,
  isCurrentUserQuestionnaireComplete,
  partnerName,
  user,
  areAccountsLinked,
  onViewHistory,
  showHistoryLink,
  onNavigateToIncompleteSection,
  currentUserHasFinancials,
  partnerHasFinancials,
}: NegotiationHeaderProps) => {
  const [sendQuestionnaireReminder, { isLoading: isSendingReminder }] =
    useSendQuestionnaireReminderMutation();

  const handleSendReminder = async () => {
    try {
      if (user?.id) {
        await sendQuestionnaireReminder({
          userId: user.id,
        }).unwrap();
      }
    } catch (error) {
      console.error("Failed to send reminder:", error);
    }
  };

  const getSubtitle = () => {
    if (!isCurrentUserQuestionnaireComplete) {
      return "You still have some questionnaire sections to complete. Please finish your questionnaire before proceeding to alignment with your partner.";
    }
    if (!currentUserHasFinancials) {
      return "You still need to complete your financial disclosure. Please finish your financial disclosure before proceeding to alignment with your partner.";
    }
    if (!isPartnerQuestionnaireComplete) {
      return `Uh Oh! Looks like ${partnerName} hasn't completed their questionnaire yet. Before you can resolve any disputes, both parties need to complete their questionnaires.`;
    }
    if (!partnerHasFinancials) {
      return `Uh Oh! Looks like ${partnerName} hasn't completed their financial disclosure yet. Before you can resolve any disputes, both parties need to complete their financial disclosure.`;
    }
    if (hasDisputes) {
      return "Welcome to Alignment. If you're here, it's because you and your partner had differing answers for some of the questions. Let's align on what you and your partner would like to do by resolving your differences below.";
    }
    return "Great news! You and your partner are on the same page. Since you selected the same options, there's nothing else you need to resolve to move forward.";
  };

  const renderReminderButton = () => {
    return (
      <StyledButtonContainer>
        <PrimaryButton
          onClick={handleSendReminder}
          disabled={isSendingReminder}
        >
          {isSendingReminder ? "Sending..." : "Send reminder email"}
        </PrimaryButton>
      </StyledButtonContainer>
    );
  };

  const renderCompleteQuestionnaireButton = () => {
    return (
      <StyledButtonContainer>
        <PrimaryButton
          onClick={onNavigateToIncompleteSection}
          disabled={!onNavigateToIncompleteSection}
        >
          Complete questionnaire
        </PrimaryButton>
      </StyledButtonContainer>
    );
  };

  return (
    <>
      <StyledHeader>Alignment</StyledHeader>
      <StyledSubtitle>{getSubtitle()}</StyledSubtitle>

      {showHistoryLink && (
        <StyledLink
          onClick={onViewHistory}
          style={{
            fontSize: FONT_SIZE_SMALL,
            display: "block",
            marginBottom: SPACING.LG,
            textDecoration: "underline",
          }}
        >
          View alignment history
        </StyledLink>
      )}

      {!isCurrentUserQuestionnaireComplete &&
        renderCompleteQuestionnaireButton()}

      {areAccountsLinked &&
        isCurrentUserQuestionnaireComplete &&
        currentUserHasFinancials &&
        (!isPartnerQuestionnaireComplete || !partnerHasFinancials) &&
        renderReminderButton()}
    </>
  );
};

export default NegotiationHeader;
